import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { QueryComponent } from '../query-component';
import { Query, GroupOperator, DataField, Condition, QueryGroup } from '@deckplan.io/common/filters';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Point } from '@angular/cdk/drag-drop/drag-ref';
import { ConditionComponent } from '../condition/condition.component';
import { Theme, ThemeService } from '../../theme/theme.service';

type QueryAndId = Query & { myId?: string };

@Component({
    selector: 'app-group',
    templateUrl: './group.component.html',
    styleUrls: ['./group.component.scss']
})
export class GroupComponent implements QueryComponent, AfterViewInit, OnInit {

    @Input() public editable = true;

    @Input() public columns: DataField[] = [];

    // eslint-disable-next-line @typescript-eslint/naming-convention
    @Input() set Operator(operator: GroupOperator) {
        this.operator = operator;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    @Input() set Operands(operands: Query[]) {
        this.operands = operands.map(query => ({myId: this.generateId(), ...query}));
    }

    @Input() public myId = 'root';

    @Input() public parentDropLists: string[] = [];

    @Output() public remove = new EventEmitter();

    @Output() public newDropList = new EventEmitter<string>();

    @Output() public dropListRemoved = new EventEmitter<string>();

    @ViewChildren(ConditionComponent) public conditionComponents: QueryList<QueryComponent>;
    @ViewChildren(GroupComponent) public groupComponents: QueryList<QueryComponent>;

    public operands: QueryAndId[] = [];

    public operator: GroupOperator;

    public themeIsLightColor: boolean;

    private readonly childDropLists: string[] = [];

    private readonly groupQueryComponents: Set<QueryComponent> = new Set<QueryComponent>();
    private readonly conditionQueryComponents: Set<QueryComponent> = new Set<QueryComponent>();

    constructor(private readonly themeService: ThemeService) {
    }

    public ngOnInit(): void {
        // Initialize our theme color
        this.updateForTheme(this.themeService.theme());

        this.themeService.themeChanges.subscribe(this.updateForTheme.bind(this));
    }

    private updateForTheme(theme: Theme) {
        // We could simply set this to 'theme === Theme.Light' but I want to make it
        // easy to adapt this to additional theme options later.
        if (theme === Theme.Light) {
            this.themeIsLightColor = true;
        } else if (theme === Theme.Dark) {
            this.themeIsLightColor = false;
        }
    }

    public ngAfterViewInit(): void {
        this.conditionComponents.changes.subscribe(v => {
            this.conditionQueryComponents.clear();
            v.forEach(component => this.conditionQueryComponents.add(component));
        });

        this.groupComponents.changes.subscribe(v => {
            this.groupQueryComponents.clear();
            v.forEach(component => this.groupQueryComponents.add(component));
        });
    }

    public switchOperator() {
        if (this.operator.toLowerCase() === 'and') {
            this.operator = 'or';
        } else {
            this.operator = 'and';
        }
    }

    public add(operator?: GroupOperator, subDropListId?: string): void {
        // eslint-disable-next-line no-magic-numbers
        if (!operator) {
            this.operands.push({dataField: null, value: '', operation: undefined});
        } else {
            this.operands.push({operator, operands: [], myId: subDropListId});
            this.childDropLists.push(subDropListId);
            this.newDropList.emit(subDropListId);
        }
    }

    public generateId(): string {
        // eslint-disable-next-line no-magic-numbers
        return Math.random().toString(36).substring(5);
    }

    public removeOperand(i: number): void {
        this.dropListRemoved.emit(this.operands[i].myId);
        this.operands.splice(i, 1);
    }

    public removeMe(): void {
        this.remove.emit();
    }

    public toQuery(): Query {
        if (!this.groupQueryComponents.size && !this.conditionQueryComponents.size) {
            return null;
        }

        const operands = [];
        for (const qc of this.groupQueryComponents) {
            operands.push(qc.toQuery());
        }

        for (const qc of this.conditionQueryComponents) {
            operands.push(qc.toQuery());
        }

        return {
            operator: this.operator,
            operands
        } as QueryGroup;
    }

    public isCondition(query: any) {
        return query.dataField !== undefined;
    }

    public conditionUpdated(i: number, newCondition: Condition) {
        const condition = this.operands[i] as Condition;
        condition.dataField = newCondition.dataField;
        condition.operation = newCondition.operation;
        condition.value = newCondition.value;
        this.operands[i] = condition;
    }

    public reorder(event: CdkDragDrop<Query[]>) {
        if (event.previousContainer.id === this.myId) {
            moveItemInArray(this.operands, event.previousIndex, event.currentIndex);
        } else {
            this.operands.push(event.previousContainer.data[event.previousIndex]);
            event.previousContainer.data.splice(event.previousIndex, 1);
            moveItemInArray(this.operands, this.operands.length - 1, event.currentIndex);
        }
    }

    public childAddedDropList(id: string) {
        this.childDropLists.push(id);
        this.newDropList.emit(id);
    }

    public dropLists(): string[] {
        return this.parentDropLists.concat(this.childDropLists).concat([this.myId]);
    }

    /// This function is a predicate factory that helps with
    /// making sure the CdkDrag plays nice with nested drop lists
    /// Source: https://stackoverflow.com/questions/53271834/nested-drag-and-drop-in-with-angular-7-material-cdk#answer-53272842
    public canDropPredicate(): any {
        const me = this;
        return (drag: CdkDrag<Query>, drop: CdkDropList<Query[]>): boolean => {
            const fromBounds = drag.dropContainer.element.nativeElement.getBoundingClientRect();
            const toBounds = drop.element.nativeElement.getBoundingClientRect();

            if (!me.intersect(fromBounds, toBounds)) {
                return true;
            }

            // This gross but allows us to access a private field for now.
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const pointerPosition: Point = drag['_dragRef']['_pointerPositionAtLastDirectionChange'];
            // They Intersect with each other so we need to do some calculations here.
            if (me.insideOf(fromBounds, toBounds)) {
                return !me.pointInsideOf(pointerPosition, fromBounds);
            }

            return me.insideOf(toBounds, fromBounds) && me.pointInsideOf(pointerPosition, toBounds);
        };
    }

    public intersect(r1: DOMRect | ClientRect, r2: DOMRect | ClientRect): boolean {
        return !(r2.left > r1.right ||
            r2.right < r1.left ||
            r2.top > r1.bottom ||
            r2.bottom < r1.top);
    }

    public insideOf(innerRect: DOMRect | ClientRect, outerRect: DOMRect | ClientRect): boolean {
        return innerRect.left >= outerRect.left &&
            innerRect.right <= outerRect.right &&
            innerRect.top >= outerRect.top &&
            innerRect.bottom <= outerRect.bottom &&
            !(
                innerRect.left === outerRect.left &&
                innerRect.right === outerRect.right &&
                innerRect.top === outerRect.top &&
                innerRect.bottom === outerRect.bottom
            );
    }

    public pointInsideOf(position: Point, rect: DOMRect | ClientRect) {
        return position.x >= rect.left &&
            position.x <= rect.right &&
            position.y >= rect.top &&
            position.y <= rect.bottom;
    }

}
